import { ndash } from '@ipfabric/frontend-shared-assets/utils/characters';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { FloatingList } from '@ipfabric/ui-components/List/FloatingList/FloatingList';
import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { date } from '@ipfabric/utils';
import { Button as OldButton } from 'components/Button/Button';
import { Diff } from 'components/Diff/Diff';
import { ModalFullContent } from 'components/ModalFullContent/ModalFullContent';
import type { CompareManagementConfigurationConfig } from 'pages/Management/Configuration/types';
import { type FC, useState } from 'react';
import { apiEndpointToBaseUrl } from 'utils/apiEndpointToFullUrl';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useTimezone } from 'utils/hooks/useTimeZone';
import { renderQueries } from 'utils/react-query/renderQuery';
import { ConfigurationDiffModalTitle } from './ConfigurationDiffModalTitle';
import {
  useConfigContentQuery,
  useConfigurationDiffOptionsQuery,
} from './queries';

type Props = {
  leftConfig: CompareManagementConfigurationConfig;
  rightConfig: CompareManagementConfigurationConfig;
  onClose: () => void;
};

export const ConfigurationDiffModal: FC<Props> = (props) => {
  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();

  const [leftConfig, setLeftConfig] =
    useState<CompareManagementConfigurationConfig>(props.leftConfig);
  const [rightConfig, setRightConfig] =
    useState<CompareManagementConfigurationConfig>(props.rightConfig);

  const configOptions = useConfigurationDiffOptionsQuery(
    props.leftConfig,
    props.rightConfig,
  );

  const leftConfigContent = useConfigContentQuery(
    leftConfig.sn,
    leftConfig.hash,
  );

  const rightConfigContent = useConfigContentQuery(
    rightConfig.sn,
    rightConfig.hash,
  );

  const renderConfigTitle = (config: CompareManagementConfigurationConfig) => (
    <div className='flex flex-1 justify-between'>
      <span>
        <strong>{config.sn}</strong>
        {` (${config.hostname}) ${ndash} `}
        {date.formatUtcTimestampInTimeZone(
          config.lastChangeAt,
          timezone,
          dateTimeFormat,
        )}
      </span>

      <FloatingList
        items={[
          {
            label: 'Download full config',
            href: apiEndpointToBaseUrl(
              `/tables/management/configuration/download?hash=${config.hash}&sn=${config.sn}`,
            ),
          },
          {
            label: 'Download sanitized config',
            href: apiEndpointToBaseUrl(
              `/tables/management/configuration/download?hash=${config.hash}&sn=${config.sn}&sanitized=true`,
            ),
          },
        ]}
      >
        {(setRef, setOpen) => (
          <Link
            renderAs='button'
            icon='fa-download'
            iconPosition='start'
            size='small'
            forwardRef={setRef}
            onClick={() => setOpen(true)}
          >
            Download
          </Link>
        )}
      </FloatingList>
    </div>
  );

  return (
    <ModalFullContent>
      <ScrollView contentClassName='flex flex-col'>
        <PageLayout className='flex flex-col gap-4'>
          <div>
            <OldButton
              type='button'
              variant='secondary'
              icon='fa-angle-left'
              onClick={props.onClose}
            >
              Back to config list
            </OldButton>
          </div>
          {renderQueries({
            queries: {
              leftConfigContent,
              rightConfigContent,
              configOptions,
            },
            renderStaleDataOnMount: false,
            success: ({
              leftConfigContent,
              rightConfigContent,
              configOptions,
            }) => (
              <Diff
                content={[
                  {
                    text: leftConfigContent,
                    renderTitle: () => renderConfigTitle(leftConfig),
                    timestamp: leftConfig.lastChangeAt,
                  },
                  {
                    text: rightConfigContent,
                    renderTitle: () => renderConfigTitle(rightConfig),
                    timestamp: rightConfig.lastChangeAt,
                  },
                ]}
                title={
                  <ConfigurationDiffModalTitle
                    leftConfig={leftConfig}
                    rightConfig={rightConfig}
                    leftConfigOptions={configOptions.data.filter(
                      (x) => x.sn === props.leftConfig.sn,
                    )}
                    rightConfigOptions={configOptions.data.filter(
                      (x) => x.sn === props.rightConfig.sn,
                    )}
                    onLeftConfigSelected={(config) => {
                      setLeftConfig(config);
                    }}
                    onRightConfigSelected={(config) => {
                      setRightConfig(config);
                    }}
                  />
                }
              />
            ),
          })}
        </PageLayout>
      </ScrollView>
    </ModalFullContent>
  );
};
